import React, { useState } from 'react';
import { connect } from 'react-redux';
import { Box, ListItem, ListItemText, ToggleButton, ToggleButtonGroup, Tooltip } from '@mui/material';
import { createActions } from '../../../../store';
import { appOperations } from '../../../../../../../App/store';
import ColoredSnackbarConstants from '../../../../../../misc/ColoredSnackbar/ColoredSnackbarConstants';
import { getEmojiImagePath, getUnicodeIdFromEmojiUrl } from '../../../../../../../utilities/emojiUtils';
import { validateParticleImage } from '../../../../../../../utilities/imageUtilities';
import { PARTICLE_ASSETS } from '../../../../Preview/elements/particles/ParticleAssets';
import { PARTICLE_SHAPE_TYPES, MEDIA_USAGE_TYPE, MEDIA_SOURCE_TYPE } from '../../../../CreatePageConstants';
import EmojiPickerPopover from './EmojiPickerPopover';
import { DEFAULT_EMOJI_UNICODE } from '../constants/EmojiConstants';
import { mediaPickerActions } from '../../../../MediaPickers/store';

const ParticlesShapeListItem = (props) => {
    const { mediaData, shape, index, updateParticleElement, openSnack, showImageCropper } = props;
    const [anchorEl, setAnchorEl] = useState(null);
    const [selectedEmojiUnicode, setSelectedEmojiUnicode] = useState(
        (shape === PARTICLE_SHAPE_TYPES.EMOJI && mediaData)
            ? (getUnicodeIdFromEmojiUrl(mediaData) ?? DEFAULT_EMOJI_UNICODE)
            : DEFAULT_EMOJI_UNICODE
    );

    const isOpen = Boolean(anchorEl);
    const handleOpenEmoji = (event) => setAnchorEl(event.currentTarget);
    const handleCloseEmoji = () => setAnchorEl(null);

    const getCurrentShape = () => {
        return shape;
    };

    const handleShapeChange = (event, newShape) => {
        if (!newShape) return;

        const baseShapes = [PARTICLE_SHAPE_TYPES.CIRCLE, PARTICLE_SHAPE_TYPES.HEART, PARTICLE_SHAPE_TYPES.STAR, PARTICLE_SHAPE_TYPES.NOTE];

        if (baseShapes.includes(newShape)) {
            updateParticleElement(index, { shape: newShape, mediaData: null });
        } else if (newShape === PARTICLE_SHAPE_TYPES.EMOJI) {
            handleOpenEmoji(event);
        }
    };

    const handleDoneEmoji = () => {
        if (shape !== PARTICLE_SHAPE_TYPES.EMOJI || mediaData !== getEmojiImagePath(selectedEmojiUnicode)) {
            applyEmoji(selectedEmojiUnicode);
        }
        handleCloseEmoji();
    };

    const applyEmoji = (unicodeId) => {
        setSelectedEmojiUnicode(unicodeId); 
        const emojiUrl = getEmojiImagePath(unicodeId);
        updateParticleElement(index, {  
            shape: PARTICLE_SHAPE_TYPES.EMOJI,
            mediaData: emojiUrl,
            color: '0xfbfbfb'
        });
    };

    const handleImageUpload = async (event) => {
        const file = event.target.files[0];
        if (!file) return;

        try {
            const imageDataUrl = await validateParticleImage(file);
            
            showImageCropper(
                imageDataUrl,
                'png',
                MEDIA_SOURCE_TYPE.UPLOADED,
                MEDIA_USAGE_TYPE.PARTICLE,
                null,
                null,
                index
            );
        } catch (error) {
            openSnack(error, ColoredSnackbarConstants.SNACKBAR_ERROR);
        }

        event.target.value = '';
    };

    return (
        <>
            <ListItem>
                <ListItemText primary="Shape" />
                <ToggleButtonGroup value={getCurrentShape()} exclusive onChange={handleShapeChange} size="small">
                    <ToggleButton value={PARTICLE_SHAPE_TYPES.CIRCLE} sx={{ minWidth: 40 }}>
                        <img src={PARTICLE_ASSETS.CIRCLE_BASIC} alt="Circle" style={{ width: 24, height: 24 }} />
                    </ToggleButton>
                    <ToggleButton value={PARTICLE_SHAPE_TYPES.HEART} sx={{ minWidth: 40 }}>
                        <img src={PARTICLE_ASSETS.HEART_BASIC} alt="Heart" style={{ width: 20, height: 20 }} />
                    </ToggleButton>
                    <ToggleButton value={PARTICLE_SHAPE_TYPES.STAR} sx={{ minWidth: 40 }}>
                        <img src={PARTICLE_ASSETS.STAR_BASIC} alt="Star" style={{ width: 24, height: 24 }} />
                    </ToggleButton>
                    <ToggleButton value={PARTICLE_SHAPE_TYPES.NOTE} sx={{ minWidth: 40 }}>
                        <img src={PARTICLE_ASSETS.NOTE_BASIC} alt="Note" style={{ width: 24, height: 24 }} />
                    </ToggleButton>
                    <ToggleButton value={PARTICLE_SHAPE_TYPES.EMOJI} onClick={(e) => getCurrentShape() === PARTICLE_SHAPE_TYPES.EMOJI && handleOpenEmoji(e)} sx={{ minWidth: 40 }}>
                        <img src={(shape === PARTICLE_SHAPE_TYPES.EMOJI && mediaData) ? mediaData : getEmojiImagePath(selectedEmojiUnicode)} alt="emoji" style={{ width: 18, height: 18 }} />
                    </ToggleButton>
                    <Tooltip title="Recommended media size: 128 x 128" placement="top" arrow>
                        <ToggleButton 
                            value={PARTICLE_SHAPE_TYPES.CUSTOM} 
                            onClick={() => document.getElementById(`particle-shape-upload-${index}`).click()}
                            sx={{ minWidth: 48 }}
                        >
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>Upload</Box>
                        </ToggleButton>
                    </Tooltip>
                </ToggleButtonGroup>
            </ListItem>

            <input
                accept="image/png,image/jpeg,image/webp"
                style={{ display: 'none' }}
                id={`particle-shape-upload-${index}`}
                type="file"
                onChange={handleImageUpload}
            />

            <EmojiPickerPopover 
                isOpen={isOpen}
                anchorEl={anchorEl}
                onClose={handleCloseEmoji}
                onDone={handleDoneEmoji}
                selectedEmojiUnicode={selectedEmojiUnicode}
                onSelect={applyEmoji}
                uniqueId={index}
            />
        </>
    );
};

const mapStateToProps = (state, ownProps) => {
    const particleElement = state.createPreview.present.videoSettings.elements.particles.items[ownProps.index];
    return { 
        shape: particleElement.shape,
        mediaData: particleElement.mediaData
    };
};

const mapDispatchToProps = (dispatch) => ({
    updateParticleElement: (index, properties) => dispatch(createActions.updateParticleElement(index, properties)),
    openSnack: (text, variant) => dispatch(appOperations.openSnack(text, variant)),
    showImageCropper: (data, format, mediaSourceType, mediaUsageType, mediaResourceId, mediaResourceUrl, particleIndex) => 
        dispatch(mediaPickerActions.showImageCropper(data, format, mediaSourceType, mediaUsageType, mediaResourceId, mediaResourceUrl, particleIndex))
});

export default connect(mapStateToProps, mapDispatchToProps)(ParticlesShapeListItem);